import React, { useEffect, useMemo, useState } from 'react';
import { getErrorMessage, isUnauthorizedError } from '../services/http';
import { ReportsAPI } from '../services/reports';
import { TasksAPI } from '../services/tasks';
import OperationStatus from './OperationStatus';

const REPORT_TYPES = [
  { value: 'task_productivity', label: 'Produtividade Task' },
  { value: 'auvo_tasks', label: 'Tarefas AUVO' },
  { value: 'auvo_os', label: 'OS AUVO' },
  { value: 'bling_proposals', label: 'Propostas Bling' },
  { value: 'client_usage', label: 'Uso de Clientes' },
  { value: 'product_usage', label: 'Uso de Produtos' }
];

const TASK_PRODUCTIVITY_COLUMNS = [
  'userName',
  'title',
  'customerName',
  'status',
  'stage',
  'openedAt',
  'closedAt',
  'duration',
  'closeReason'
];

const AUVO_TASK_COLUMNS = [
  'Codigo',
  'Data',
  'Cliente',
  'Responsavel',
  'Tipo da tarefa',
  'Finalizada',
  'Relato',
  'Pendencias',
  'Total de valores da tarefa(R$)'
];

const AUVO_OS_COLUMNS = [
  'numero_os',
  'cliente_nome',
  'status',
  'data_primeira_visita',
  'qtd_produtos',
  'qtd_servicos',
  'total_os',
  'atualizado_em'
];

const BLING_COLUMNS = [
  'code',
  'label',
  'customerName',
  'status',
  'date',
  'amount',
  'url'
];

const USAGE_COLUMNS = [
  'tipoCadastro',
  'nome',
  'origem',
  'idExterno',
  'documentos',
  'totalDocumentos',
  'ultimoUso',
  'recomendacao'
];

function todayOffset(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function createFilters() {
  return {
    dataInicial: todayOffset(-30),
    dataFinal: todayOffset(0),
    cliente: '',
    palavrasChave: '',
    numero: '',
    limit: 200
  };
}

function formatCellValue(value) {
  if (value === null || typeof value === 'undefined' || value === '') return '-';
  if (typeof value === 'number') return new Intl.NumberFormat('pt-BR').format(value);
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return new Date(value).toLocaleString('pt-BR');
  }
  return String(value);
}

function formatDuration(ms) {
  const totalSeconds = Math.round(Number(ms || 0) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours) return `${hours}h ${minutes}min`;
  if (minutes) return `${minutes}min ${seconds}s`;
  return `${seconds}s`;
}

function getColumns(type, report) {
  if (type === 'task_productivity') return TASK_PRODUCTIVITY_COLUMNS;
  if (type === 'auvo_tasks') return AUVO_TASK_COLUMNS;
  if (type === 'auvo_os') return AUVO_OS_COLUMNS;
  if (type === 'bling_proposals') return BLING_COLUMNS;
  if (type === 'client_usage' || type === 'product_usage') return USAGE_COLUMNS;
  return report?.headers?.slice(0, 10) || [];
}

function flattenUsageRows(payload, type) {
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  return rows.flatMap((record) => {
    const links = Array.isArray(record.vinculos) ? record.vinculos : [];
    if (!links.length) {
      return [{
        tipoCadastro: type === 'client_usage' ? 'Cliente' : 'Produto',
        nome: record.nome || '',
        origem: '-',
        idExterno: '-',
        documentos: '-',
        totalDocumentos: 0,
        ultimoUso: '',
        recomendacao: 'sem_vinculo'
      }];
    }

    return links.map((link) => ({
      tipoCadastro: type === 'client_usage' ? 'Cliente' : 'Produto',
      nome: record.nome || '',
      origem: link.origem || '',
      idExterno: link.idExterno || '',
      documentos: Object.entries(link.documentos || {}).map(([key, value]) => `${key}: ${value}`).join(', '),
      totalDocumentos: link.totalDocumentos || 0,
      ultimoUso: link.ultimoUso || '',
      recomendacao: link.recomendacao || ''
    }));
  });
}

function getOverviewCount(overview, path) {
  return path.reduce((value, key) => value?.[key], overview) ?? 0;
}

function escapeCsv(value) {
  const text = String(value ?? '');
  if (!/[",\n;]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsv(report, columns, reportType) {
  const rows = Array.isArray(report?.rows) ? report.rows : [];
  const csvRows = [
    columns.join(';'),
    ...rows.map((row) => columns.map((column) => escapeCsv(row?.[column])).join(';'))
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${reportType}-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function ReportsPanel({ onClose, onUnauthorized }) {
  const [activeType, setActiveType] = useState('task_productivity');
  const [filters, setFilters] = useState(() => createFilters());
  const [overview, setOverview] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const columns = useMemo(() => getColumns(activeType, report), [activeType, report]);
  const rows = Array.isArray(report?.rows) ? report.rows : [];

  const handleRequestError = (requestError, fallbackMessage) => {
    if (isUnauthorizedError(requestError)) {
      onUnauthorized?.();
      return;
    }

    setError(getErrorMessage(requestError, fallbackMessage));
  };

  const loadOverview = async () => {
    try {
      const payload = await ReportsAPI.overview();
      setOverview(payload?.overview || payload);
    } catch (_error) {
      setOverview(null);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    setError('');

    try {
      let payload = null;
      if (activeType === 'task_productivity') {
        const productivity = await TasksAPI.productivityReport({
          dataInicial: filters.dataInicial,
          dataFinal: filters.dataFinal,
          limit: filters.limit
        });
        payload = {
          type: 'task_productivity',
          total: productivity?.total || 0,
          summary: productivity?.summary || {},
          rows: (Array.isArray(productivity?.rows) ? productivity.rows : []).map((row) => ({
            userName: row.user?.name || row.user?.id || '',
            title: row.title || '',
            customerName: row.customerName || '',
            status: row.status || '',
            stage: row.stage || '',
            openedAt: row.openedAt || '',
            closedAt: row.closedAt || '',
            duration: formatDuration(row.durationMs),
            durationMs: Number(row.durationMs || 0),
            closeReason: row.closeReason || (row.active ? 'aberta agora' : '')
          }))
        };
      } else if (activeType === 'auvo_tasks') {
        payload = await ReportsAPI.auvoTasks({
          dataInicial: filters.dataInicial,
          dataFinal: filters.dataFinal,
          cliente: filters.cliente,
          palavrasChave: filters.palavrasChave,
          limit: filters.limit
        });
      } else if (activeType === 'auvo_os') {
        payload = await ReportsAPI.auvoOs({
          since: filters.dataInicial,
          numero: filters.numero,
          cliente: filters.cliente,
          limit: filters.limit
        });
      } else if (activeType === 'bling_proposals') {
        payload = await ReportsAPI.blingProposals({
          number: filters.numero
        });
      } else if (activeType === 'client_usage') {
        const usage = await ReportsAPI.clientUsage({
          search: filters.cliente || filters.numero,
          limit: filters.limit
        });
        payload = {
          type: 'client_usage',
          total: usage?.total || 0,
          rows: flattenUsageRows(usage, 'client_usage')
        };
      } else if (activeType === 'product_usage') {
        const usage = await ReportsAPI.productUsage({
          search: filters.cliente || filters.numero,
          limit: filters.limit
        });
        payload = {
          type: 'product_usage',
          total: usage?.total || 0,
          rows: flattenUsageRows(usage, 'product_usage')
        };
      }

      setReport(payload);
    } catch (requestError) {
      handleRequestError(requestError, 'Não foi possível carregar o relatório.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOverview();
    void loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setReport(null);
    setError('');
  }, [activeType]);

  return (
    <aside className="task-overlay-panel task-overlay-panel--wide shadow-lg">
      <div className="overlay-panel-header">
        <div>
          <div className="eyebrow">CEC Integra</div>
          <h2>Relatórios</h2>
          <p className="text-muted mb-0">Acompanhe produtividade do Task e consulte dados sincronizados do AUVO e do Bling.</p>
        </div>
        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Fechar</button>
      </div>

      <div className="compact-panel-grid mt-4">
        <div className="header-kpi">
          <span>Propostas Bling</span>
          <strong>{getOverviewCount(overview, ['bling', 'propostas'])}</strong>
        </div>
        <div className="header-kpi">
          <span>Clientes AUVO</span>
          <strong>{getOverviewCount(overview, ['auvo', 'clientes'])}</strong>
        </div>
        <div className="header-kpi">
          <span>OS AUVO</span>
          <strong>{getOverviewCount(overview, ['auvo', 'ordens'])}</strong>
        </div>
      </div>

      <div className="task-tabs mt-4">
        {REPORT_TYPES.map((type) => (
          <button
            key={type.value}
            type="button"
            className={`task-tab ${activeType === type.value ? 'task-tab--active' : ''}`}
            onClick={() => setActiveType(type.value)}
          >
            {type.label}
          </button>
        ))}
      </div>

      <form className="report-filters mt-4" onSubmit={(event) => { event.preventDefault(); void loadReport(); }}>
        {activeType !== 'bling_proposals' && (
          <>
            <div>
              <label className="form-label">{activeType === 'auvo_os' ? 'Atualizado desde' : 'Data inicial'}</label>
              <input
                className="form-control"
                type="date"
                value={filters.dataInicial}
                onChange={(event) => setFilters((current) => ({ ...current, dataInicial: event.target.value }))}
              />
            </div>
            {(activeType === 'auvo_tasks' || activeType === 'task_productivity') && (
              <div>
                <label className="form-label">Data final</label>
                <input
                  className="form-control"
                  type="date"
                  value={filters.dataFinal}
                  onChange={(event) => setFilters((current) => ({ ...current, dataFinal: event.target.value }))}
                />
              </div>
            )}
            {activeType !== 'task_productivity' && (
              <div>
                <label className="form-label">Cliente</label>
                <input
                  className="form-control"
                  value={filters.cliente}
                  onChange={(event) => setFilters((current) => ({ ...current, cliente: event.target.value }))}
                  placeholder={activeType === 'product_usage' ? 'Nome, SKU ou ID do produto' : 'Nome, documento ou ID do cliente'}
                />
              </div>
            )}
          </>
        )}

        {(activeType === 'bling_proposals' || activeType === 'auvo_os') && (
          <div>
            <label className="form-label">{activeType === 'bling_proposals' ? 'Número da proposta' : 'Número da OS'}</label>
            <input
              className="form-control"
              value={filters.numero}
              onChange={(event) => setFilters((current) => ({ ...current, numero: event.target.value }))}
              placeholder={activeType === 'bling_proposals' ? 'Ex.: 12345' : 'Ex.: OS-123'}
            />
          </div>
        )}

        {activeType === 'auvo_tasks' && (
          <div>
            <label className="form-label">Palavra-chave</label>
            <input
              className="form-control"
              value={filters.palavrasChave}
              onChange={(event) => setFilters((current) => ({ ...current, palavrasChave: event.target.value }))}
              placeholder="Relato, pendência ou orientação"
            />
          </div>
        )}

        {activeType !== 'bling_proposals' && (
          <div>
            <label className="form-label">Limite</label>
            <input
              className="form-control"
              type="number"
              min="1"
              max="1000"
              value={filters.limit}
              onChange={(event) => setFilters((current) => ({ ...current, limit: event.target.value }))}
            />
          </div>
        )}

        <div className="report-filters__actions">
          <button className="btn btn-primary" type="submit" disabled={loading || (activeType === 'bling_proposals' && !filters.numero.trim())}>
            {loading ? 'Carregando...' : 'Gerar relatório'}
          </button>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={() => downloadCsv(report, columns, activeType)}
            disabled={!rows.length}
          >
            Exportar CSV
          </button>
        </div>
      </form>

      <OperationStatus
        active={loading}
        title="Gerando relatório"
        steps={[
          activeType === 'task_productivity' && 'Consultando produtividade das tarefas.',
          activeType === 'auvo_tasks' && 'Consultando tarefas AUVO pelo cec_api_integra.',
          activeType === 'auvo_os' && 'Consultando OS AUVO pelo cec_api_integra.',
          activeType === 'bling_proposals' && 'Consultando proposta Bling pelo cec_api_integra.',
          activeType === 'client_usage' && 'Calculando uso de clientes nos documentos integrados.',
          activeType === 'product_usage' && 'Calculando uso de produtos nos documentos integrados.',
          'Preparando tabela e totalizadores.',
        ]}
      />

      {error && <div className="alert alert-danger mt-3">{error}</div>}

      {activeType === 'task_productivity' && report?.summary && (
        <div className="report-summary-grid mt-4">
          <div className="header-kpi">
            <span>Usuários com análise</span>
            <strong>{report.summary.users?.length || 0}</strong>
          </div>
          <div className="header-kpi">
            <span>Tarefas analisadas</span>
            <strong>{report.summary.tasks?.length || 0}</strong>
          </div>
          <div className="header-kpi">
            <span>Sem análise</span>
            <strong>{report.summary.tasksWithoutAnalysis?.length || 0}</strong>
          </div>
        </div>
      )}

      <div className="report-table-wrap mt-4">
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-2">
          <strong>{Number(report?.total || rows.length)} registros</strong>
          {report?.type && <span className="text-muted small">{report.type}</span>}
        </div>
        <table className="report-table">
          <thead>
            <tr>
              {columns.map((column) => <th key={column}>{column}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`${report?.type || activeType}-${rowIndex}`}>
                {columns.map((column) => (
                  <td key={column}>
                    {column === 'url' && row?.[column]
                      ? <a href={row[column]} target="_blank" rel="noreferrer">Abrir</a>
                      : formatCellValue(row?.[column])}
                  </td>
                ))}
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={Math.max(columns.length, 1)} className="text-muted">
                  {loading ? 'Carregando dados...' : 'Nenhum dado carregado para este filtro.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </aside>
  );
}
